/**
 * /library/worksheet/[id] — Worksheet Detail (Phase 4.3).
 *
 * Full preview + tags + AI explanation + 3 action buttons:
 *  - Cetak          → print PDF (Phase 4.4)
 *  - Simpan PDF     → download PDF (Phase 4.4)
 *  - Tandai Selesai → REAL: POST /v1/worksheet/:id/complete
 *
 * (Kirim WA dihapus 2026-08-19 — menyederhanakan pilihan aksi untuk user.)
 *
 * Loads: GET /v1/worksheet/:id?child_id=N (full detail + approval row)
 * Updates: POST /v1/worksheet/:id/complete?child_id=N
 *
 * The screen is reachable from:
 *  - HistoryCard tap on Riwayat tab (Phase 4.2 wired it)
 *  - (Phase 4.5) Home card stack "Lihat Worksheet" sticky CTA
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { getActiveChild } from '@/lib/api/children';
import { getPreferences } from '@/lib/api/preferences';
import {
  getWorksheetDetail,
  markComplete,
  toHistoryStatus,
  type WorksheetDetail,
  type WorksheetApproved,
} from '@/lib/api/worksheet';
import { generateExplanation } from '@/lib/explanation';
import {
  printWorksheet,
  downloadPdf,
  recordPrintStatus,
  buildPdfFilename,
} from '@/lib/print';
import { ApiError } from '@/lib/api/client';

import { WorksheetHero } from '@/components/library/WorksheetHero';
import { WorksheetTags } from '@/components/library/WorksheetTags';
import { ExplanationBox } from '@/components/library/ExplanationBox';
import { DetailActions } from '@/components/library/DetailActions';
import { StatusBadge } from '@/components/library/StatusBadge';
import { Toast } from '@/components/library/Toast';

import type { ChildProfile } from '@funpaper/shared';

type DetailState =
  | { kind: 'loading' }
  | { kind: 'ready'; child: ChildProfile; worksheet: WorksheetDetail; approved: WorksheetApproved | null }
  | { kind: 'error'; message: string };

export default function WorksheetDetailScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ id: string }>();
  const worksheetId = Number.parseInt(params.id ?? '', 10);

  const [state, setState] = useState<DetailState>({ kind: 'loading' });
  const [toast, setToast] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<'print' | 'pdf' | null>(null);
  const actionInFlightRef = useRef(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --------------------------------------------------------------------------
  // Toast helper
  // --------------------------------------------------------------------------

  const showToast = useCallback((message: string, durationMs = 1800) => {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, durationMs);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // --------------------------------------------------------------------------
  // Load detail
  // --------------------------------------------------------------------------

  const loadDetail = useCallback(async () => {
    if (!Number.isFinite(worksheetId) || worksheetId <= 0) {
      setState({ kind: 'error', message: 'ID worksheet tidak valid.' });
      return;
    }

    let child: ChildProfile | null = null;
    try {
      child = await getActiveChild();
    } catch {
      setState({ kind: 'error', message: 'Gagal memuat data anak.' });
      return;
    }
    if (!child) {
      setState({ kind: 'error', message: 'Belum ada profil anak. Selesaikan onboarding dulu.' });
      return;
    }

    try {
      const { worksheet, approved } = await getWorksheetDetail(worksheetId, child.id);
      setState({ kind: 'ready', child, worksheet, approved });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Gagal memuat worksheet.';
      setState({ kind: 'error', message });
    }
  }, [worksheetId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  // --------------------------------------------------------------------------
  // Action handlers
  // --------------------------------------------------------------------------

  const onCetak = useCallback(async () => {
    if (state.kind !== 'ready' || actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setActionInProgress('print');
    showToast('Membuka jendela cetak…');
    try {
      const result = await printWorksheet(state.worksheet.pdf_url);
      if (result.ok) {
        await recordPrintStatus(state.worksheet.id, state.child.id, 'printed');
        // Re-fetch to update the status badge.
        try {
          const fresh = await getWorksheetDetail(state.worksheet.id, state.child.id);
          setState({ ...state, approved: fresh.approved });
        } catch { /* ignore */ }
      } else {
        showToast(result.error);
      }
    } finally {
      actionInFlightRef.current = false;
      setActionInProgress(null);
    }
  }, [state, showToast]);

  const onSimpanPdf = useCallback(async () => {
    if (state.kind !== 'ready' || actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setActionInProgress('pdf');
    const filename = buildPdfFilename(state.worksheet.name, state.worksheet.id);
    showToast('Menyimpan PDF…');
    try {
      const result = await downloadPdf(state.worksheet.id, state.worksheet.pdf_url, filename);
      if (result.ok) {
        await recordPrintStatus(state.worksheet.id, state.child.id, 'shared_pdf');
        try {
          const fresh = await getWorksheetDetail(state.worksheet.id, state.child.id);
          setState({ ...state, approved: fresh.approved });
        } catch { /* ignore */ }
        showToast('✓ PDF siap disimpan / dibagikan');
      } else {
        showToast(result.error);
      }
    } finally {
      actionInFlightRef.current = false;
      setActionInProgress(null);
    }
  }, [state, showToast]);

  const onSelesai = useCallback(async () => {
    if (state.kind !== 'ready') return;
    if (state.approved?.completedAt) return; // already done
    setCompleting(true);
    try {
      await markComplete(state.worksheet.id, state.child.id);
      // Re-fetch to update completedAt + status badge.
      const fresh = await getWorksheetDetail(state.worksheet.id, state.child.id);
      setState({ ...state, approved: fresh.approved });
      showToast('✓ Worksheet ditandai selesai!');
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'Gagal menandai selesai. Coba lagi.';
      showToast(message);
    } finally {
      setCompleting(false);
    }
  }, [state, showToast]);

  const onBack = useCallback(() => {
    router.back();
  }, [router]);

  // --------------------------------------------------------------------------
  // Preferred tema for the AI explanation (best-effort)
  // --------------------------------------------------------------------------

  // Hooks must live above the early returns below — calling useState/useEffect
  // after a conditional return changes the hook count between renders and
  // crashes React ("Rendered more hooks than during the previous render").
  const [preferredTema, setPreferredTema] = useState<string[]>([]);
  const childId = state.kind === 'ready' ? state.child.id : null;
  useEffect(() => {
    if (childId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const prefs = await getPreferences(childId);
        if (!cancelled) setPreferredTema(prefs.preferredTema);
      } catch {
        // ignore — explanation will use 'baru kenal' for everything
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [childId]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (state.kind === 'loading') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <DetailHeader onBack={onBack} title="" />
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.kind === 'error') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <DetailHeader onBack={onBack} title="Error" />
          <View style={styles.errorContainer}>
            <ThemedText type="subtitle" style={styles.errorTitle}>
              Gagal memuat worksheet
            </ThemedText>
            <ThemedText type="default" themeColor="textSecondary" style={styles.errorBody}>
              {state.message}
            </ThemedText>
            <Pressable onPress={() => void loadDetail()} style={styles.retryBtn}>
              <Text style={styles.retryText}>Coba lagi</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  // Ready state.
  const { child, worksheet, approved } = state;

  const explanation = generateExplanation(
    { name: child.name, age: child.age, preferredTema },
    {
      name: worksheet.name,
      temaNames: worksheet.temaNames,
      activityLabel: worksheet.activity_label,
      durationMinutes: worksheet.duration_minutes,
      themeLabel: worksheet.theme_label,
    },
  );

  const historyStatus = approved
    ? toHistoryStatus(approved.printStatus, approved.completedAt)
    : null;
  const alreadyCompleted = approved?.completedAt != null;

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <DetailHeader onBack={onBack} title={worksheet.name} />
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Hero preview */}
          <View style={styles.heroSection}>
            <WorksheetHero worksheet={worksheet} />
          </View>

          {/* Title + status badge */}
          <View style={styles.titleSection}>
            <ThemedText type="subtitle" style={styles.title}>
              {worksheet.name}
            </ThemedText>
            {historyStatus ? <StatusBadge status={historyStatus} /> : null}
          </View>

          {/* Tags */}
          <View style={styles.tagsSection}>
            <WorksheetTags worksheet={worksheet} />
          </View>

          {/* AI explanation */}
          <View style={styles.explanationSection}>
            <ExplanationBox childName={child.name} explanation={explanation} />
          </View>

          {/* Action buttons */}
          <DetailActions
            onSelesai={onSelesai}
            onCetak={onCetak}
            onSimpanPdf={onSimpanPdf}
            alreadyCompleted={alreadyCompleted}
            completing={completing}
            actionInProgress={actionInProgress}
          />

          {/* Approved-at hint (when present) */}
          {approved ? (
            <View style={styles.approvedHint}>
              <Text style={styles.approvedHintText}>
                Disetujui pada {formatDateTime(approved.approvedAt)}
              </Text>
            </View>
          ) : null}
        </ScrollView>
        <Toast message={toast} />
      </SafeAreaView>
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// DetailHeader
// ----------------------------------------------------------------------------

function DetailHeader({ onBack, title }: { onBack: () => void; title: string }) {
  return (
    <View style={styles.header}>
      <Pressable
        onPress={onBack}
        style={({ pressed }) => [styles.backBtn, pressed && styles.backBtnPressed]}
        accessibilityRole="button"
        accessibilityLabel="Kembali ke Aktivitas"
      >
        <Text style={styles.backArrow}>←</Text>
      </Pressable>
      <Text style={styles.headerTitle} numberOfLines={1}>
        {title || ' '}
      </Text>
      <View style={styles.headerSpacer} />
    </View>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function formatDateTime(isoOrSql: string): string {
  // The server returns "YYYY-MM-DD HH:MM:SS" in Asia/Jakarta.
  // We split and re-format to "27 Jul, 10:30".
  const cleaned = isoOrSql.replace('T', ' ').replace('Z', '');
  const [datePart, timePart] = cleaned.split(' ');
  if (!datePart || !timePart) return isoOrSql;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return isoOrSql;
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
    'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des',
  ];
  const monthLabel = monthNames[m - 1] ?? '';
  const [hh, mm] = timePart.split(':');
  return `${d} ${monthLabel}, ${hh}:${mm}`;
}

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  scrollContent: {
    paddingBottom: Spacing.five,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: Radii.small,
  },
  backBtnPressed: {
    backgroundColor: '#f5f3ee',
  },
  backArrow: {
    fontSize: 24,
    color: Brand.primary,
    fontWeight: '600',
  },
  headerTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: '#1A0B5C',
    textAlign: 'center',
    marginHorizontal: Spacing.two,
  },
  headerSpacer: { width: 40 },
  heroSection: {
    paddingTop: Spacing.three,
  },
  titleSection: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    gap: Spacing.two,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1A0B5C',
  },
  tagsSection: {
    paddingTop: Spacing.three,
  },
  explanationSection: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.four,
  },
  approvedHint: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
  },
  approvedHintText: {
    fontSize: 12,
    color: '#6b6573',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    gap: Spacing.three,
  },
  errorTitle: {
    color: Brand.primary,
    textAlign: 'center',
  },
  errorBody: {
    textAlign: 'center',
    maxWidth: 320,
  },
  retryBtn: {
    marginTop: Spacing.two,
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.two,
    backgroundColor: Brand.primary,
    borderRadius: Radii.medium,
  },
  retryText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
});
